import React from 'react';
import { Box, Button, IconButton, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTileSourceCatalog } from '../../../hooks/toolbox-hooks';

export function PathAndParamsEditor({
                                      source,
                                      entity,
                                      models,
                                      path,
                                      onPathChange,
                                      params,
                                      onParamsChange,
                                      availableVariables = [],
                                    }: {
  source: string;
  entity: string;
  models: { db_table: string; verbose_name: string }[];
  path: string;
  onPathChange: (path: string) => void;
  params: Record<string, string>;
  onParamsChange: (params: Record<string, string>) => void;
  availableVariables?: string[];
}) {
  const { sourceMapByLabel } = useTileSourceCatalog();

  const selectedSource = sourceMapByLabel?.[source];
  const entityOptions = React.useMemo(() => selectedSource?.entities || [], [selectedSource]);

  React.useEffect(() => {
    if (!entity) return;
    if (path && path.trim().length > 0) return;

    let suggestedPath = '';

    if (source === 'Toolbox DB') {
      const model = models.find((m) => m.db_table === entity);
      if (model) suggestedPath = `db/${model.db_table}`;
    } else {
      const found = entityOptions.find((e: any) => e.value === entity);
      if (found?.path) suggestedPath = found.path;
    }

    if (suggestedPath) onPathChange(suggestedPath);
  }, [source, entity, path, models, entityOptions, onPathChange]);

  const paramEntries = Object.entries(params || {});

  function handleParamKeyChange(idx: number, newKey: string) {
    const entries = [...paramEntries];
    entries[idx] = [newKey, entries[idx][1]];
    onParamsChange(Object.fromEntries(entries));
  }

  function handleParamValueChange(idx: number, newValue: string) {
    const entries = [...paramEntries];
    entries[idx] = [entries[idx][0], newValue];
    onParamsChange(Object.fromEntries(entries));
  }

  function handleAddParam() {
    onParamsChange({ ...params, '': '' });
  }

  function handleRemoveParam(idx: number) {
    const entries = [...paramEntries];
    entries.splice(idx, 1);
    onParamsChange(Object.fromEntries(entries));
  }

  return (
    <Box mt={2}>
      <TextField
        label="Request Path"
        value={path}
        onChange={(e) => onPathChange(e.target.value)}
        fullWidth
        helperText="Supports variables like {user_id}, {course_id}, etc."
      />
      <Box mt={1}>
        <div style={{ fontWeight: 500, marginBottom: 6 }}>Parameters</div>
        {paramEntries.map(([key, value], idx) => (
          <Box key={idx} display="flex" alignItems="center" gap={1} mb={1}>
            <TextField
              label="Name"
              value={key}
              onChange={(e) => handleParamKeyChange(idx, e.target.value)}
              size="small"
            />
            <TextField
              label="Value"
              value={value}
              onChange={(e) => handleParamValueChange(idx, e.target.value)}
              size="small"
              helperText={availableVariables.length ? 'Supports variables' : ''}
            />
            <IconButton size="small" onClick={() => handleRemoveParam(idx)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <Button onClick={handleAddParam} size="small" startIcon={<AddIcon />}>
          Add Parameter
        </Button>
      </Box>
    </Box>
  );
}